// fields/SearchSelectField.js
// ============================================
// API-based search select field with debouncing
// ============================================
import React, { useState, useEffect, useRef, useCallback } from 'react';

export const SearchSelectField = ({ 
  field, 
  register, 
  errors, 
  theme, 
  setValue, 
  watch,
  searchEndpoint, // API endpoint for search
  searchParams = {}, // Additional params to send with search
  minSearchLength = 2, // Minimum characters before searching
  debounceMs = 300, // Debounce delay
  placeholder = "Type to search...",
  displayFormat = "full",
  customDisplay = null,
  allowClear = true,
  maxDropdownHeight = "300px",
  loadingText = "Searching...",
  noResultsText = "No results found",
  searchErrorText = "Error loading results",
  transformResponse = null, // Function to transform API response
  getOptionValue = (option) => option.id, // Function to get option value
  getOptionLabel = (option) => option.displayName, // Function to get option label
  getOptionInfo = (option) => option.info, // Function to get option info
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceTimer = useRef(null);
  const abortController = useRef(null);
  
  // Store props in refs to avoid recreating performSearch
  const searchEndpointRef = useRef(searchEndpoint);
  const searchParamsRef = useRef(searchParams);
  const minSearchLengthRef = useRef(minSearchLength);
  const transformResponseRef = useRef(transformResponse);
  
  // Update refs when props change
  useEffect(() => {
    searchEndpointRef.current = searchEndpoint;
    searchParamsRef.current = searchParams;
    minSearchLengthRef.current = minSearchLength;
    transformResponseRef.current = transformResponse;
  }, [searchEndpoint, searchParams, minSearchLength, transformResponse]);
  
    const fieldTheme = theme.fields?.searchSelect || theme.fields?.select || {};
    const dropdownTheme = theme.dropdown || {};

  const currentValue = watch(field.id);
  
  // Load initial value if provided
  useEffect(() => {
    if (currentValue && !selectedItem) {
      loadInitialValue(currentValue);
    }
  }, [currentValue]);
  
  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);
  
  // Load initial value from API if needed
  const loadInitialValue = async (id) => {
    try {
      const response = await fetch(`${searchEndpointRef.current}/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const item = transformResponseRef.current ? transformResponseRef.current([data])[0] : data;
        setSelectedItem(item);
      }
    } catch (error) {
      console.error('Failed to load initial value:', error);
    }
  };
  
  // Stable search function that doesn't cause re-renders
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < minSearchLengthRef.current) {
      setSearchResults([]);
      return;
    }
    
    // Cancel previous request
    if (abortController.current) {
      abortController.current.abort();
    }
    
    // Create new abort controller
    abortController.current = new AbortController();
    
    setIsLoading(true);
    setSearchError(null);
    
    try {
      const params = new URLSearchParams({
        q: query,
        ...searchParamsRef.current,
      });
      
      const response = await fetch(`${searchEndpointRef.current}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.current.signal,
      });
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform response if transformer provided
      const results = transformResponseRef.current ? transformResponseRef.current(data) : data;
      
      setSearchResults(Array.isArray(results) ? results : []);
      setHighlightedIndex(0);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        setSearchError(error.message);
        setSearchResults([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array - function never changes
  
  // Handle search input changes with debouncing
  useEffect(() => {
    // Don't search if term is empty or too short
    if (!searchTerm || searchTerm.length < minSearchLength) {
      setSearchResults([]);
      return;
    }
    
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Set new timer for search
    debounceTimer.current = setTimeout(() => {
      performSearch(searchTerm);
    }, debounceMs);
    
    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm, minSearchLength, debounceMs, performSearch]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    
    if (!isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        scrollToHighlighted();
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        scrollToHighlighted();
        break;
        
      case 'Enter':
        e.preventDefault();
        if (searchResults[highlightedIndex]) {
          handleSelect(searchResults[highlightedIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        if (!selectedItem) {
          setSearchTerm('');
        }
        break;
        
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  
  const scrollToHighlighted = () => {
    setTimeout(() => {
      const highlighted = dropdownRef.current?.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  };
  
  const handleSelect = (option) => {
    setSelectedItem(option);
    setValue(field.id, getOptionValue(option), { shouldValidate: true });
    setSearchTerm('');
    setSearchResults([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };
  
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedItem(null);
    setValue(field.id, '', { shouldValidate: true });
    setSearchTerm('');
    setSearchResults([]);
  };
  
  const handleInputClick = () => {
    if (!selectedItem) {
      setIsOpen(true);
      if (searchTerm.length >= minSearchLength) {
        performSearch(searchTerm);
      }
    } else {
      // Clear selection and open for new search
      setSelectedItem(null);
      setValue(field.id, '', { shouldValidate: false });
      setIsOpen(true);
    }
  };
  
  const getDisplayText = (item) => {
    if (!item) return '';
    
    if (customDisplay) {
      return customDisplay(item);
    }
    
    const label = getOptionLabel(item);
    const info = getOptionInfo(item);
    
    switch (displayFormat) {
      case 'nameOnly':
        return label;
      case 'full':
        return info ? `${label} - ${info}` : label;
      default:
        return label;
    }
  };
  
  const getInputClassName = () => {
    const classes = [
      fieldTheme.base || theme.input,
      'pr-10'
    ];
    
    if (isFocused) classes.push(fieldTheme.focus);
    if (errors[field.id]) classes.push(fieldTheme.error || theme.inputError);
    if (field.disabled) classes.push(fieldTheme.disabled);
    if (selectedItem) classes.push(fieldTheme.filled);
    if (isLoading) classes.push('animate-pulse');
    
    return classes.join(' ');
  };
  
  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          value={selectedItem ? getDisplayText(selectedItem) : searchTerm}
          onChange={(e) => {
            if (selectedItem) {
              setSelectedItem(null);
              setValue(field.id, '', { shouldValidate: false });
            }
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onClick={handleInputClick}
          onFocus={() => {
            setIsFocused(true);
            if (!selectedItem && searchTerm.length >= minSearchLength) {
              setIsOpen(true);
            }
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={selectedItem ? '' : placeholder}
          className={getInputClassName()}
          disabled={field.disabled}
          autoComplete="off"
        />
        
        {/* Icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Loading spinner */}
          {isLoading && (
            <svg className="animate-spin h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          
          {/* Clear button */}
          {!isLoading && allowClear && selectedItem && !field.disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {/* Search/Dropdown icon */}
          {!isLoading && (
            <svg 
              className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {selectedItem || searchResults.length > 0 ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              )}
            </svg>
          )}
        </div>
      </div>
      
{/* Dropdown */}
{isOpen && (
  <div 
    ref={dropdownRef}
    className={dropdownTheme.container || 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-auto'}
    style={{ maxHeight: maxDropdownHeight }}
  >
    {/* Loading state */}
    {isLoading && (
      <div className={dropdownTheme.loadingContainer || 'px-3 py-4 text-center text-gray-500 dark:text-gray-400'}>
        <svg 
          className={dropdownTheme.loadingSpinner || 'animate-spin h-5 w-5 mx-auto mb-2'} 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {loadingText}
      </div>
    )}
    
    {/* Error state */}
    {!isLoading && searchError && (
      <div className={dropdownTheme.errorText || 'px-3 py-2 text-red-500 dark:text-red-400 text-center'}>
        {searchErrorText}
      </div>
    )}
    
    {/* Results */}
    {!isLoading && !searchError && searchResults.length > 0 && (
      <>
        {searchResults.map((option, index) => (
          <div
            key={getOptionValue(option)}
            data-highlighted={index === highlightedIndex}
            onClick={() => handleSelect(option)}
            onMouseEnter={() => setHighlightedIndex(index)}
            className={
              index === highlightedIndex 
                ? (dropdownTheme.itemHighlighted || 'px-3 py-2 cursor-pointer transition-colors bg-blue-50 dark:bg-blue-900/30')
                : (dropdownTheme.item || 'px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700')
            }
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className={dropdownTheme.itemLabel || 'font-medium text-gray-900 dark:text-gray-100'}>
                  {getOptionLabel(option)}
                </div>
                {getOptionInfo(option) && (
                  <div className={dropdownTheme.itemInfo || 'text-sm text-gray-500 dark:text-gray-400 mt-0.5'}>
                    {getOptionInfo(option)}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </>
    )}
    
    {/* No results */}
    {!isLoading && !searchError && searchResults.length === 0 && searchTerm.length >= minSearchLength && (
      <div className={dropdownTheme.noResultsText || 'px-3 py-2 text-gray-500 dark:text-gray-400 text-center'}>
        {noResultsText}
      </div>
    )}
    
    {/* Prompt to search */}
    {!isLoading && searchTerm.length < minSearchLength && searchTerm.length > 0 && (
      <div className={dropdownTheme.promptText || 'px-3 py-2 text-gray-500 dark:text-gray-400 text-center'}>
        Type at least {minSearchLength} characters to search
      </div>
    )}
  </div>
)}
      
      {/* Hidden input for form value */}
      <input
        type="hidden"
        {...register(field.id, field.validation)}
      />
    </div>
  );
};